import { useEffect, MutableRefObject } from 'react';

import { useFieldProps, useForm, useFormInputStyles } from '@hilma/netina2all';

import { useAddressNotAcc, useDeleteLatLng, useGeocode, useI18n } from '../../../../hooks';

import InputAdornment from '@material-ui/core/InputAdornment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import Input from '@material-ui/core/Input';
import Grid from '@material-ui/core/Grid';

import parse from 'autosuggest-highlight/parse';
import clsx from 'clsx';

import { AutocompletePrediction, autocompleteService } from './MapAddressSearch'

interface AutocompleteAddressInputProps {
    isLoading: boolean;
    selectedAddress: AutocompletePrediction | null;
    setSelectedAddress: React.Dispatch<React.SetStateAction<AutocompletePrediction | null>>;
    inputValue: string;
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
    options: google.maps.places.AutocompletePrediction[];
    setOptions: React.Dispatch<React.SetStateAction<google.maps.places.AutocompletePrediction[]>>;
    innerRef: MutableRefObject<Record<string, any>>;
};

const AutocompleteAddressInput: React.FC<AutocompleteAddressInputProps> = ({ isLoading, selectedAddress, setSelectedAddress, inputValue, setInputValue, options, setOptions, innerRef }) => {

    const form = useForm();
    const inputprops = useFieldProps('address');

    const geocode = useGeocode();
    const deleteLatLng = useDeleteLatLng();
    const [, setAddressNotAcc] = useAddressNotAcc();

    const placeholder = useI18n(i18n => i18n.sm.address.acPlaceholder)

    const classes = useStyles()
    const formInputStyles = useFormInputStyles();

    const fetch = () => {
        if (isLoading) return
        let active = true;

        if (!autocompleteService.current && window.google) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) return undefined;

        if (inputValue === '') {
            setOptions(selectedAddress ? [selectedAddress] : []);
            return undefined;
        }

        autocompleteService.current!.getPlacePredictions({ input: inputValue }, (results?: AutocompletePrediction[]) => {
            if (active) {
                let newOptions: AutocompletePrediction[] = [];
                if (selectedAddress) newOptions = [selectedAddress];
                if (results) newOptions = [...newOptions, ...results.filter(x => x.place_id !== selectedAddress?.place_id)];
                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }

    useEffect(() => {
        fetch();
    }, [selectedAddress, inputValue, isLoading]);

    return (
        <div className={classes.addressInputContainer}>
            <Autocomplete
                id="address"
                className={classes.addressInput}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                filterOptions={(x) => x}
                options={options}
                // autoComplete
                includeInputInList
                // filterSelectedOptions
                value={selectedAddress}
                getOptionSelected={(option: AutocompletePrediction, value: AutocompletePrediction) => option.place_id === value.place_id}
                onChange={(_event: React.ChangeEvent<{}>, newValue: AutocompletePrediction | null) => {
                    setOptions(newValue ? [newValue, ...options.filter(x => x.place_id !== newValue?.place_id)] : options);
                    geocode({ placeId: newValue?.place_id })
                    setAddressNotAcc(false)
                    setSelectedAddress(newValue);
                    form.setFieldValue('address', newValue ? newValue?.description : "");
                    form.setFieldValue('inputOrMap', 'input');
                    if (!newValue) deleteLatLng()
                }}
                onInputChange={(_event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                    <div
                        onBlur={inputprops.onBlur}
                        ref={params.InputProps.ref}
                        id="address-field">
                        <div
                            onBlur={() => {
                                form.setFieldValue('address', selectedAddress ? selectedAddress?.description : "");
                            }}
                            onFocus={fetch}
                        >
                            <FormControl fullWidth={true} className={clsx(classes.input, formInputStyles.input)}>
                                <Input
                                    type="text"
                                    disableUnderline
                                    inputRef={(el) => { if (el && innerRef) innerRef.current['address'!] = el }}
                                    inputProps={{ ...params.inputProps, form: { autocomplete: 'off' } }}
                                    {...params.InputProps}
                                    {...inputprops}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchIcon className={classes.icon} />
                                        </InputAdornment>
                                    }
                                    placeholder={placeholder}
                                />
                            </FormControl>
                        </div>
                    </div>
                )}
                renderOption={(option) => {
                    const matches = option.structured_formatting.main_text_matched_substrings;
                    const parts = parse(
                        option.structured_formatting.main_text,
                        matches ? matches.map((match: any) => [match.offset, match.offset + match.length]) : [],
                    );

                    return (
                        <Grid container alignItems="center">
                            <Grid item>
                                <LocationOnIcon className={classes.icon} />
                            </Grid>
                            <Grid item xs>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                        {part.text}
                                    </span>
                                ))}
                                <Typography variant="body2" color="textSecondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    );
                }}
            />
        </div>
    );
}

export default AutocompleteAddressInput

export const useStyles = makeStyles((theme) => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(0),
        marginLeft: theme.spacing(1),
    },

    addressInputContainer: {
        width: '100%',
        position: 'absolute',
        top: '5px',
        display: 'flex',
        justifyContent: 'center',
    },

    addressInput: {
        zIndex: 9,
        width: '90%',
    },

    input: {
        color: theme.palette.text.secondary,
        backgroundColor: '#ffffff',
        boxShadow: '0px 3px 6px #00000029',

        '& input': {
            color: theme.palette.text.secondary,
            padding: 'unset',
            fontFamily: 'Assistant-Regular',
        },
    },
}));
